import React from 'react';
import { useTranslation } from 'react-i18next';

import { SubscriptionTopic } from '../../../../../../../viewer/messaging/pubsub';
import { DesignTool } from '../../../../../../../viewer/tools/design-tool';
import {
  openCreatePointByCoordinateDialog,
  openCreatePointByOffsetDialog,
} from '../../../../../../../viewer/tools/utils/context-menu/design-tool/commands/point/create-from-dialog';
import { useSubscribe } from '../../../../../../hooks/use-subscribe';
import { Button } from '../../../../../button/button';
import { InfoBox } from '../../../../../info-box/info-box';
import { Stack } from '../../../../../stack/stack';
import { useCurrentSelection } from '../hooks/use-current-selection';

type Props = {
  tool: DesignTool;
};

const CreatePoints = ({ tool }: Props) => {
  const { t } = useTranslation();
  const [, refresh] = React.useReducer((count: number) => count + 1, 0);

  // The offset depends on the line being created and the selected points.
  useSubscribe(SubscriptionTopic.DesignToolActiveActionChanged, refresh);
  useCurrentSelection(tool);

  const coordinate = openCreatePointByCoordinateDialog(tool);
  // The cursor is on the menu, so a selected point is used before the last hovered point.
  const offset = openCreatePointByOffsetDialog(tool, { includeHoveredPoint: false });

  return (
    // Refresh when entering the menu, since the cursor position in the scene may have changed.
    <div onMouseEnter={refresh}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={0.5}>
          <Button
            color="primary"
            variant="contained"
            width="100%"
            onClick={() => coordinate.action()}
          >
            {t('section.functions.createPoints.coordinateButton', {
              ns: 'skyviewDesign',
              hotkey: coordinate.hotkey,
            })}
          </Button>
          <Button
            color="primary"
            disabled={offset.disabled}
            variant="contained"
            width="100%"
            onClick={() => offset.action()}
          >
            {t('section.functions.createPoints.offsetButton', {
              ns: 'skyviewDesign',
              hotkey: offset.hotkey,
            })}
          </Button>
        </Stack>

        {offset.disabled && (
          <InfoBox color="blue" leftIcon={{ icon: 'info' }}>
            {t('section.functions.createPoints.offsetInfo', { ns: 'skyviewDesign' })}
          </InfoBox>
        )}
      </Stack>
    </div>
  );
};

export { CreatePoints };
